import React from 'react';
import Icon from '../../../components/AppIcon';

/**
 * Downstream onboarding strip for one supplier request.
 *
 * The requester used to see only their own request row — approved, then
 * silence. This renders what actually happened next (invite -> registration ->
 * documents -> approvals -> vendor code) from /supplier-requests/journey-status,
 * so "where is my vendor?" is answered on the card itself.
 *
 * journey shape: { request_status, vendor_invited, invite, vendor, documents,
 * approvals, acknowledgement } — see the backend route for field meanings.
 */

// Visual states for one step of the strip.
const STATE_STYLE = {
  done: { circle: 'bg-emerald-100 text-emerald-700 border-emerald-300', bar: 'bg-emerald-300', label: 'text-slate-700' },
  current: { circle: 'bg-blue-100 text-blue-700 border-blue-300', bar: 'bg-slate-200', label: 'text-blue-700 font-semibold' },
  attention: { circle: 'bg-amber-100 text-amber-700 border-amber-300', bar: 'bg-slate-200', label: 'text-amber-700 font-semibold' },
  blocked: { circle: 'bg-red-100 text-red-700 border-red-300', bar: 'bg-slate-200', label: 'text-red-700 font-semibold' },
  upcoming: { circle: 'bg-slate-100 text-slate-400 border-slate-200', bar: 'bg-slate-200', label: 'text-slate-400' },
};

/**
 * Reduce the raw journey facts to the five display steps. Each returned step:
 * { key, label, icon, state, detail? } — `state` picks STATE_STYLE, `detail`
 * feeds the status line under the strip (first non-done step wins).
 */
export function deriveJourneySteps(journey, requestStatus) {
  const reqStatus = (journey?.request_status || requestStatus || '').toUpperCase();
  const steps = [];

  // 1. Request
  if (reqStatus === 'REJECTED') {
    steps.push({ key: 'request', label: 'Request', icon: 'FileX', state: 'blocked', detail: 'Request was rejected.' });
  } else if (reqStatus === 'APPROVED') {
    steps.push({ key: 'request', label: 'Request', icon: 'FileCheck', state: 'done' });
  } else {
    steps.push({
      key: 'request', label: 'Request', icon: 'FileClock', state: 'current',
      detail: reqStatus === 'SUBMITTED' ? 'Awaiting request approval.' : 'Request is still a draft.',
    });
  }

  // 2. Vendor invited
  const invited = Boolean(journey?.vendor_invited);
  const inviteUsed = Boolean(journey?.invite?.used);
  const inviteExpired =
    journey?.invite?.expires_at && !inviteUsed && new Date(journey.invite.expires_at) < new Date();
  if (invited) {
    steps.push({ key: 'invited', label: 'Invited', icon: 'Send', state: 'done' });
  } else {
    steps.push({
      key: 'invited', label: 'Invited', icon: 'Send',
      state: reqStatus === 'APPROVED' ? 'current' : 'upcoming',
      detail: reqStatus === 'APPROVED' ? 'Approved — invite not sent yet.' : undefined,
    });
  }

  // 3. Vendor registered
  const vendor = journey?.vendor;
  if (vendor) {
    steps.push({ key: 'registered', label: 'Registered', icon: 'UserCheck', state: 'done' });
  } else {
    steps.push({
      key: 'registered', label: 'Registered', icon: 'UserCheck',
      state: invited ? (inviteExpired ? 'attention' : 'current') : 'upcoming',
      detail: invited
        ? inviteExpired
          ? 'Invite expired before the vendor registered.'
          : 'Waiting for the vendor to fill the registration form.'
        : undefined,
    });
  }

  // 4. Documents
  const docs = journey?.documents;
  if (vendor && docs && docs.total > 0) {
    if (docs.rejected > 0) {
      steps.push({
        key: 'documents', label: 'Documents', icon: 'FileWarning', state: 'attention',
        detail: `${docs.rejected} document${docs.rejected === 1 ? '' : 's'} rejected — replacement requested from the vendor.`,
      });
    } else if (docs.pending > 0) {
      steps.push({
        key: 'documents', label: 'Documents', icon: 'FileSearch', state: 'current',
        detail: `Documents under verification: ${docs.verified} of ${docs.total} verified.`,
      });
    } else {
      steps.push({ key: 'documents', label: 'Documents', icon: 'FileCheck2', state: 'done' });
    }
  } else {
    steps.push({ key: 'documents', label: 'Documents', icon: 'FileSearch', state: 'upcoming' });
  }

  // 5. Approvals + code. LEVEL_2 approval auto-creates FINAL, so FINAL alone is
  // not shown as a separate wait state; the visible tail is the vendor code.
  const approvals = journey?.approvals || {};
  const ack = journey?.acknowledgement;
  const hasCode = Boolean(vendor?.vendor_code);
  if (hasCode) {
    steps.push({
      key: 'approved', label: 'Code Assigned', icon: 'BadgeCheck', state: 'done',
      detail: `Onboarding complete — vendor code ${vendor.vendor_code}.`,
    });
  } else if (vendor && (vendor.status || '').toUpperCase() === 'REJECTED') {
    steps.push({ key: 'approved', label: 'Approval', icon: 'ShieldX', state: 'blocked', detail: 'Vendor was rejected during approval.' });
  } else if (approvals.LEVEL_2 === 'APPROVED' || approvals.FINAL === 'APPROVED') {
    steps.push({ key: 'approved', label: 'Code Assigned', icon: 'BadgeCheck', state: 'current', detail: 'Fully approved — vendor code being assigned.' });
  } else if (approvals.LEVEL_1 === 'APPROVED') {
    const ackDone = ack && (ack.used || (ack.status || '').toUpperCase() === 'APPROVED');
    steps.push({
      key: 'approved', label: 'Approval', icon: 'ShieldCheck', state: 'current',
      detail: ackDone
        ? 'Stage 2 approval in progress.'
        : 'Stage 1 approved — awaiting vendor acknowledgement of commercial terms.',
    });
  } else if (approvals.LEVEL_1 === 'PENDING') {
    steps.push({ key: 'approved', label: 'Approval', icon: 'ShieldCheck', state: 'current', detail: 'Stage 1 approval in progress.' });
  } else {
    steps.push({ key: 'approved', label: 'Approval', icon: 'ShieldCheck', state: 'upcoming' });
  }

  return steps;
}

const RequestJourney = ({ journey, requestStatus }) => {
  const steps = deriveJourneySteps(journey, requestStatus);
  // The status line explains the first step that still needs something.
  const active = steps.find((s) => s.state !== 'done' && s.detail) || steps.filter((s) => s.detail).pop();

  return (
    <div className="mt-1 mb-4">
      <div className="flex items-center">
        {steps.map((step, i) => {
          const style = STATE_STYLE[step.state];
          return (
            <React.Fragment key={step.key}>
              {i > 0 && <div className={`h-0.5 flex-1 mx-1 ${STATE_STYLE[steps[i - 1].state === 'done' ? 'done' : 'upcoming'].bar}`} />}
              <div className="flex flex-col items-center min-w-0" title={step.detail || step.label}>
                <div className={`h-8 w-8 rounded-full border flex items-center justify-center ${style.circle}`}>
                  <Icon name={step.state === 'done' ? 'Check' : step.icon} size={15} />
                </div>
                <span className={`mt-1 text-[11px] whitespace-nowrap ${style.label}`}>{step.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {active?.detail && (
        <p className="mt-2 text-xs text-slate-500 text-center">{active.detail}</p>
      )}
    </div>
  );
};

export default RequestJourney;
